import { z } from "zod";

/**
 * Shared filter types and schemas for search/filter functionality
 * Single source of truth for all filtering across the platform
 */

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Persona/Audience demographic filters
 */
export interface PersonaFilters {
  fanGender?: string[];
  fanEthnicity?: string[];
  fanAge?: string[];
  fanIncome?: string[];
  fanAttributes?: string[];
  fanGeo?: string[];
}

/**
 * Complete search filter interface
 * Used across: home search, advanced filters, personas, browse pages
 */
export interface SearchFilters {
  // Name/Search filters
  artistName?: string;
  venueName?: string;
  marketName?: string;
  
  // Location filters
  location?: string;
  city?: string;
  state?: string;
  targetDmaIds?: number[];  // Hard filter: DMA IDs (resolved from targetLocations)
  
  // Date filters
  dateFrom?: string;
  dateTo?: string;
  
  // Artist filters
  genre?: string | string[];  // Support both comma-separated and array
  careerStage?: string | string[];  // Support both comma-separated and array
  setArtist?: string;  // "yes" or "no" - filters by set_artist_group_id presence

  // Venue filters
  venueType?: string | string[];  // Support both comma-separated and array
  venueLocation?: string;
  
  // Show filters
  sponsorable?: boolean;
  
  // Persona/audience matching
  personaFilters?: PersonaFilters;
  minMatchPercentage?: number;
  
  // Database IDs (for internal use)
  artistId?: number;
  venueId?: number;
  marketId?: number;
}

/**
 * Filter option structure (from filter_options table or database extraction)
 */
export interface FilterOption {
  value: string;
  label: string;
  description?: string;
  category?: string;
  isActive?: boolean;
  count?: number;  // Optional: number of items with this filter value
}

// ============================================================================
// ZOD VALIDATION SCHEMAS
// ============================================================================

/**
 * Persona filters validation
 */
export const personaFiltersSchema = z.object({
  fanGender: z.array(z.string()).optional(),
  fanEthnicity: z.array(z.string()).optional(),
  fanAge: z.array(z.string()).optional(),
  fanIncome: z.array(z.string()).optional(),
  fanAttributes: z.array(z.string()).optional(),
  fanGeo: z.array(z.string()).optional(),
}).optional();

/**
 * Search filters validation schema
 */
export const searchFiltersSchema = z.object({
  // Name/Search
  artistName: z.string().optional(),
  venueName: z.string().optional(),
  marketName: z.string().optional(),
  
  // Location
  location: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  targetDmaIds: z.array(z.number()).optional(),
  
  // Date
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  
  // Artist
  genre: z.union([z.string(), z.array(z.string())]).optional(),
  careerStage: z.union([z.string(), z.array(z.string())]).optional(),
  setArtist: z.enum(["yes", "no"]).optional(),

  // Venue
  venueType: z.union([z.string(), z.array(z.string())]).optional(),
  venueLocation: z.string().optional(),
  
  // Show
  sponsorable: z.boolean().optional(),
  
  // Persona
  personaFilters: personaFiltersSchema,
  minMatchPercentage: z.number().min(0).max(100).optional(),
  
  // IDs
  artistId: z.number().optional(),
  venueId: z.number().optional(),
  marketId: z.number().optional(),
});

/**
 * Filter option validation
 */
export const filterOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  isActive: z.boolean().optional(),
  count: z.number().optional(),
});

// ============================================================================
// FILTER NORMALIZATION UTILITIES
// ============================================================================

/**
 * Normalize filter values to arrays
 * Handles comma-separated strings and existing arrays
 */
export function normalizeToArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.split(',').map(v => v.trim()).filter(v => v);
}

/**
 * Normalize search filters for consistent processing
 * Converts comma-separated strings to arrays where applicable
 */
export function normalizeSearchFilters(filters: SearchFilters): SearchFilters {
  return {
    ...filters,
    genre: Array.isArray(filters.genre) ? filters.genre : normalizeToArray(filters.genre),
    careerStage: Array.isArray(filters.careerStage) ? filters.careerStage : normalizeToArray(filters.careerStage),
    venueType: Array.isArray(filters.venueType) ? filters.venueType : normalizeToArray(filters.venueType),
  };
}

/**
 * Check if filters object has any active filters
 */
export function hasActiveFilters(filters: SearchFilters): boolean {
  const normalized = normalizeSearchFilters(filters);
  
  return !!(
    filters.artistName ||
    filters.venueName ||
    filters.marketName ||
    filters.location ||
    filters.city ||
    filters.state ||
    (filters.targetDmaIds && filters.targetDmaIds.length > 0) ||
    filters.dateFrom ||
    filters.dateTo ||
    (normalized.genre && normalized.genre.length > 0) ||
    (normalized.careerStage && normalized.careerStage.length > 0) ||
    (normalized.venueType && normalized.venueType.length > 0) ||
    filters.venueLocation ||
    filters.sponsorable !== undefined ||
    (filters.personaFilters && Object.values(filters.personaFilters).some(arr => arr && arr.length > 0))
  );
}

/**
 * Count active filters
 */
export function countActiveFilters(filters: SearchFilters): number {
  const normalized = normalizeSearchFilters(filters);
  let count = 0;
  
  if (filters.artistName) count++;
  if (filters.venueName) count++;
  if (filters.marketName) count++;
  if (filters.location) count++;
  if (filters.city) count++;
  if (filters.state) count++;
  if (filters.targetDmaIds && filters.targetDmaIds.length > 0) count++;
  if (filters.dateFrom) count++;
  if (filters.dateTo) count++;
  if (normalized.genre && normalized.genre.length > 0) count++;
  if (normalized.careerStage && normalized.careerStage.length > 0) count++;
  if (normalized.venueType && normalized.venueType.length > 0) count++;
  if (filters.venueLocation) count++;
  if (filters.sponsorable !== undefined) count++;
  
  // Count persona filters
  if (filters.personaFilters) {
    if (filters.personaFilters.fanGender && filters.personaFilters.fanGender.length > 0) count++;
    if (filters.personaFilters.fanEthnicity && filters.personaFilters.fanEthnicity.length > 0) count++;
    if (filters.personaFilters.fanAge && filters.personaFilters.fanAge.length > 0) count++;
    if (filters.personaFilters.fanIncome && filters.personaFilters.fanIncome.length > 0) count++;
    if (filters.personaFilters.fanAttributes && filters.personaFilters.fanAttributes.length > 0) count++;
    if (filters.personaFilters.fanGeo && filters.personaFilters.fanGeo.length > 0) count++;
  }
  
  return count;
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type { SearchFilters as Filters };  // Alias for convenience
